// src/components/admin/teamTaskbox/widgets/TeamWorkloadWidget.js
import React, { useState, useEffect } from 'react';
import { Users, TrendingUp, Clock, Info, X, Save, Loader2, Database, HardDrive } from 'lucide-react';
import { useEmployees } from '../../../../contexts/EmployeeContext';
import workloadSettingsService from '../../../../services/workloadSettings.service';

const TeamWorkloadWidget = ({ tasks }) => {
    const { employees } = useEmployees();
    const [workloadData, setWorkloadData] = useState([]);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [loadingSettings, setLoadingSettings] = useState(true);
    const [savingSettings, setSavingSettings] = useState(false);

    // Workload ranges state with defaults
    const [workloadRanges, setWorkloadRanges] = useState({
        light: { min: 0, max: 19 },
        moderate: { min: 20, max: 39 },
        heavy: { min: 40, max: 999 }
    });

    // Temporary state for input changes
    const [tempRanges, setTempRanges] = useState(workloadRanges);
    const [validationError, setValidationError] = useState('');
    const [saveError, setSaveError] = useState('');
    const [storageMethod, setStorageMethod] = useState('database');

    useEffect(() => {
        calculateWorkload();
    }, [tasks, employees, workloadRanges]);

    // Fetch workload settings from database on component mount
    useEffect(() => {
        fetchWorkloadSettings();
    }, []);

    const fetchWorkloadSettings = async () => {
        try {
            setLoadingSettings(true);
            const settings = await workloadSettingsService.getWorkloadSettings();
            if (settings && typeof settings === 'object') {
                // Handle different response formats
                const ranges = settings.data || settings;
                if (ranges.light && ranges.moderate && ranges.heavy) {
                    setWorkloadRanges(ranges);
                    setTempRanges(ranges);
                }
            }

            // Update storage method indicator
            setStorageMethod(workloadSettingsService.getStorageMethod());
        } catch (error) {
            console.error('Failed to fetch workload settings:', error);
            // Keep using default ranges if fetch fails
            setStorageMethod('localStorage');
        } finally {
            setLoadingSettings(false);
        }
    };

    const calculateWorkload = () => {
        if (!tasks || tasks.length === 0) {
            setWorkloadData([]);
            return;
        }

        // Group tasks by assigned employee IDs
        const employeeTaskMap = new Map();

        tasks.forEach(task => {
            if (task.assigned_employee_ids && Array.isArray(task.assigned_employee_ids)) {
                task.assigned_employee_ids.forEach(empId => {
                    if (!employeeTaskMap.has(empId)) {
                        // Find employee details from employees context
                        const employee = employees.find(emp => emp.id === empId);

                        employeeTaskMap.set(empId, {
                            id: empId,
                            name: employee ? employee.name : `Employee ${empId.substring(0, 8)}...`, // Show actual name or shortened UUID
                            avatar: employee ? employee.photo : null,
                            tasks: [],
                            totalHours: 0,
                            completedTasks: 0,
                            pendingTasks: 0,
                            overdueTasks: 0
                        });
                    }

                    const empData = employeeTaskMap.get(empId);
                    empData.tasks.push(task);
                    empData.totalHours += parseFloat(task.estimated_hours) || 0;

                    if (task.status === 'completed') {
                        empData.completedTasks++;
                    } else {
                        empData.pendingTasks++;
                    }

                    if (task.overdue_days > 0) {
                        empData.overdueTasks++;
                    }
                });
            }
        });

        // Convert to array and sort by total hours (descending)
        const workload = Array.from(employeeTaskMap.values())
            .sort((a, b) => b.totalHours - a.totalHours)
            .slice(0, 10); // Top 10 employees

        setWorkloadData(workload);
    };

    const getWorkloadLevel = (hours) => {
        if (hours >= workloadRanges.heavy.min) return { label: 'Heavy', color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-100 dark:bg-red-900/30' };
        if (hours >= workloadRanges.moderate.min) return { label: 'Moderate', color: 'text-yellow-600 dark:text-yellow-400', bgColor: 'bg-yellow-100 dark:bg-yellow-900/30' };
        return { label: 'Light', color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-900/30' };
    };

    // Validation functions
    const validateRanges = (ranges) => {
        // Check if light max < moderate min
        if (ranges.light.max >= ranges.moderate.min) {
            return 'Light workload maximum must be less than Moderate workload minimum';
        }

        // Check if moderate max < heavy min
        if (ranges.moderate.max >= ranges.heavy.min) {
            return 'Moderate workload maximum must be less than Heavy workload minimum';
        }

        // Check if min <= max for each range
        if (ranges.light.min >= ranges.light.max) {
            return 'Light workload minimum must be less than maximum';
        }

        if (ranges.moderate.min >= ranges.moderate.max) {
            return 'Moderate workload minimum must be less than maximum';
        }

        if (ranges.heavy.min >= ranges.heavy.max) {
            return 'Heavy workload minimum must be less than maximum';
        }

        // Check for positive values
        if (ranges.light.min < 0 || ranges.moderate.min < 0 || ranges.heavy.min < 0) {
            return 'Workload hours cannot be negative';
        }

        return '';
    };

    const handleRangeChange = (level, field, value) => {
        const numValue = parseInt(value) || 0;
        setTempRanges(prev => ({
            ...prev,
            [level]: {
                ...prev[level],
                [field]: numValue
            }
        }));

        // Clear validation error when user starts typing
        if (validationError) {
            setValidationError('');
        }
    };

    const handleSaveRanges = async () => {
        const error = validateRanges(tempRanges);
        if (error) {
            setValidationError(error);
            return;
        }

        try {
            setSavingSettings(true);
            setSaveError('');

            const result = await workloadSettingsService.saveWorkloadSettings(tempRanges);

            setWorkloadRanges(tempRanges);
            setShowSettingsModal(false);
            setValidationError('');
            setSaveError('');

            // Update storage method indicator after successful save
            setStorageMethod(workloadSettingsService.getStorageMethod());

            // Show success message based on storage method
            const storageMsg = result?.source === 'localStorage'
                ? 'Settings saved locally (database unavailable)'
                : 'Settings saved successfully';
            console.log('✅', storageMsg);
        } catch (error) {
            console.error('Failed to save workload settings:', error);
            setSaveError('Failed to save settings. Please try again.');
        } finally {
            setSavingSettings(false);
        }
    };

    const handleOpenSettings = () => {
        setTempRanges(workloadRanges);
        setValidationError('');
        setSaveError('');
        setShowSettingsModal(true);
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        Team Workload
                        {loadingSettings && (
                            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                        )}
                    </h3>
                    {/* Storage Method Indicator */}
                    {!loadingSettings && (
                        <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full">
                            {storageMethod === 'database' ? (
                                <>
                                    <Database className="w-3 h-3 text-blue-600" />
                                    <span className="text-xs text-blue-600 dark:text-blue-400">Database</span>
                                </>
                            ) : (
                                <>
                                    <HardDrive className="w-3 h-3 text-orange-600" />
                                    <span className="text-xs text-orange-600 dark:text-orange-400">Local</span>
                                </>
                            )}
                        </div>
                    )}
                </div>
                <button
                    onClick={handleOpenSettings}
                    disabled={loadingSettings}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Workload Settings"
                >
                    <Info className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </button>
            </div>

            {workloadData.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                    No workload data available
                </div>
            ) : (
                <div className="space-y-4">
                    {workloadData.map((employee) => {
                        const workloadLevel = getWorkloadLevel(employee.totalHours);
                        const completionRate = employee.tasks.length > 0
                            ? Math.round((employee.completedTasks / employee.tasks.length) * 100)
                            : 0;

                        return (
                            <div
                                key={employee.id}
                                className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                            >
                                {/* Avatar */}
                                <img
                                    src={employee.avatar || '/default-avatar.png'}
                                    alt={employee.name}
                                    className="w-10 h-10 rounded-full"
                                />

                                {/* Employee Info */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-1">
                                        <h4 className="font-medium text-gray-900 dark:text-white truncate">
                                            {employee.name}
                                        </h4>
                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${workloadLevel.bgColor} ${workloadLevel.color}`}>
                                            {workloadLevel.label}
                                        </span>
                                    </div>

                                    <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400 mb-2">
                                        <span>{employee.tasks.length} tasks</span>
                                        <span className="flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            {Math.round(employee.totalHours)}h
                                        </span>
                                        {employee.overdueTasks > 0 && (
                                            <span className="text-red-600 dark:text-red-400">
                                                {employee.overdueTasks} overdue
                                            </span>
                                        )}
                                    </div>

                                    {/* Progress Bar */}
                                    <div className="flex items-center gap-2">
                                        <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                                            <div
                                                className="bg-blue-600 h-2 rounded-full transition-all"
                                                style={{ width: `${completionRate}%` }}
                                            />
                                        </div>
                                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                            {completionRate}%
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Workload Settings Modal */}
            {showSettingsModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Workload Settings
                            </h3>
                            <button
                                onClick={() => setShowSettingsModal(false)}
                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                            </button>
                        </div>

                        <div className="space-y-6">
                            {/* Light Workload Settings */}
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 bg-green-500 rounded"></div>
                                    <h4 className="font-medium text-gray-900 dark:text-white">Light Workload</h4>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Min Hours
                                        </label>
                                        <input
                                            type="number"
                                            value={tempRanges.light.min}
                                            onChange={(e) => handleRangeChange('light', 'min', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Max Hours
                                        </label>
                                        <input
                                            type="number"
                                            value={tempRanges.light.max}
                                            onChange={(e) => handleRangeChange('light', 'max', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Moderate Workload Settings */}
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                                    <h4 className="font-medium text-gray-900 dark:text-white">Moderate Workload</h4>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Min Hours
                                        </label>
                                        <input
                                            type="number"
                                            value={tempRanges.moderate.min}
                                            onChange={(e) => handleRangeChange('moderate', 'min', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Max Hours
                                        </label>
                                        <input
                                            type="number"
                                            value={tempRanges.moderate.max}
                                            onChange={(e) => handleRangeChange('moderate', 'max', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Heavy Workload Settings */}
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 bg-red-500 rounded"></div>
                                    <h4 className="font-medium text-gray-900 dark:text-white">Heavy Workload</h4>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Min Hours
                                        </label>
                                        <input
                                            type="number"
                                            value={tempRanges.heavy.min}
                                            onChange={(e) => handleRangeChange('heavy', 'min', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Max Hours
                                        </label>
                                        <input
                                            type="number"
                                            value={tempRanges.heavy.max}
                                            onChange={(e) => handleRangeChange('heavy', 'max', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Validation Error */}
                            {validationError && (
                                <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg text-sm">
                                    {validationError}
                                </div>
                            )}

                            {/* Save Error */}
                            {saveError && (
                                <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg text-sm">
                                    {saveError}
                                </div>
                            )}

                            {/* Info Note */}
                            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg text-sm">
                                <strong>Note:</strong> Workload ranges must follow this order:
                                Light Max &lt; Moderate Min &lt; Moderate Max &lt; Heavy Min
                            </div>

                            {/* Storage Method Note */}
                            {storageMethod === 'localStorage' && (
                                <div className="p-3 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 rounded-lg text-sm">
                                    <strong>Fallback Mode:</strong> Database is currently unavailable.
                                    Settings are saved locally on this device and will not sync across devices.
                                </div>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => setShowSettingsModal(false)}
                                disabled={savingSettings}
                                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveRanges}
                                disabled={savingSettings}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {savingSettings ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        Save Changes
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeamWorkloadWidget;
